// src/utils/toast.js
// Pequeno sistema de toasts não bloqueantes, alinhado ao tema global.

const MAX_VISIBLE_TOASTS = 3;
const MAX_QUEUED_TOASTS = 20;

let toastSequence = 0;
const toastQueue = [];

function ensureContainer() {
  let container = document.getElementById('app-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'app-toast-container';
    container.style.position = 'fixed';
    container.style.right = '16px';
    container.style.top = 'calc(var(--header-h, 64px) + 16px)';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.gap = '8px';
    container.style.zIndex = '9999';
    container.style.pointerEvents = 'none'; // permite clicar atrás; o toast controla seus próprios eventos
    container.setAttribute('aria-live', 'polite');
    document.body.appendChild(container);
  }
  return container;
}

function colorByType(type) {
  switch (type) {
    case 'success':
      return { bar: '#22c55e86' };
    case 'warning':
      return { bar: '#f59f0b83' };
    case 'error':
      return { bar: 'var(--danger, #ef4444af)' };
    case 'info':
    default:
      return { bar: 'var(--accent, #00ccffa9)' };
  }
}

function hasVisibleToast(container, dedupKey) {
  return Array.from(container.children).some(
    (child) =>
      child &&
      child.dataset &&
      child.dataset.message === dedupKey &&
      child.dataset.removing !== '1'
  );
}

function hasQueuedToast(dedupKey) {
  return toastQueue.some((toast) => toast.dedupKey === dedupKey);
}

function drainToastQueue() {
  if (typeof document === 'undefined') return;
  const container = ensureContainer();

  while (container.childElementCount < MAX_VISIBLE_TOASTS && toastQueue.length > 0) {
    const nextToast = toastQueue.shift();
    if (!nextToast || hasVisibleToast(container, nextToast.dedupKey)) {
      continue;
    }
    renderToast(container, nextToast);
  }
}

function removeToast(container, toast) {
  try {
    if (!toast || toast.dataset.removing === '1') return;
    toast.dataset.removing = '1';
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(8px)';
    setTimeout(() => {
      if (toast && toast.parentNode === container) container.removeChild(toast);
      drainToastQueue();
    }, 180);
  } catch {
    // Remocao visual falhou; o toast sera descartado no proximo ciclo.
  }
}

function renderToast(container, toastData) {
  const {
    id,
    dedupKey,
    titleText,
    bodyText,
    type,
    iconUrl,
    iconAlt,
    onClick,
    timeout,
  } = toastData;
  const { bar } = colorByType(type);
  const resolvedIcon = typeof iconUrl === 'string' ? iconUrl.trim() : '';
  // Elemento do toast
  const toast = document.createElement('div');
  toast.dataset.toastId = String(id);
  toast.dataset.message = dedupKey;
  toast.dataset.createdAt = String(Date.now());
  toast.setAttribute('role', 'status');
  toast.style.pointerEvents = 'auto';
  toast.style.display = 'grid';
  toast.style.gridTemplateColumns = resolvedIcon ? '6px 40px 1fr auto' : '6px 1fr auto';
  toast.style.alignItems = 'center';
  toast.style.columnGap = '12px';
  toast.style.padding = '16px 20px';
  toast.style.maxWidth = 'min(440px, calc(100vw - 32px))';
  toast.style.boxSizing = 'border-box';
  toast.style.overflow = 'hidden';
  toast.style.borderRadius = 'var(--border-radius-lg, 12px)';
  toast.style.boxShadow = 'var(--shadow-1, 0 8px 30px rgba(0, 0, 0, 0.55))';
  toast.style.background = 'rgba(13, 27, 42, 0.78)';
  toast.style.border = '1px solid var(--border, rgba(255, 255, 255, 0.18))';
  toast.style.backdropFilter = 'blur(4px)';
  toast.style.color = 'var(--text, #f1f5f9)';
  toast.style.fontSize = 'var(--font-size-sm, 14px)';
  toast.style.fontWeight = '500';
  toast.style.lineHeight = '1.2';
  toast.style.opacity = '0';
  toast.style.transform = 'translateY(-8px)';
  toast.style.transition = 'opacity 160ms ease, transform 160ms ease';

  // Barra colorida por tipo
  const barEl = document.createElement('div');
  barEl.style.width = '6px';
  barEl.style.height = '100%';
  barEl.style.borderRadius = '8px';
  barEl.style.background = bar;
  barEl.style.boxShadow = 'inset 0 0 0 1px rgba(255,255,255,0.05)';

  let iconEl = null;
  if (resolvedIcon) {
    iconEl = document.createElement('img');
    iconEl.src = resolvedIcon;
    iconEl.alt = iconAlt || (titleText ? `Foto de ${titleText}` : 'Avatar');
    iconEl.style.width = '36px';
    iconEl.style.height = '36px';
    iconEl.style.borderRadius = '999px';
    iconEl.style.objectFit = 'cover';
    iconEl.style.background = 'rgba(255,255,255,0.08)';
    iconEl.style.boxShadow = '0 0 0 1px rgba(255,255,255,0.15)';
    iconEl.style.display = 'block';
    iconEl.addEventListener('error', () => {
      if (!iconEl) return;
      iconEl.remove();
      iconEl = null;
      toast.style.gridTemplateColumns = '6px 1fr auto';
    });
  }

  const contentEl = document.createElement('div');
  contentEl.style.display = 'flex';
  contentEl.style.flexDirection = 'column';
  contentEl.style.gap = '4px';
  contentEl.style.overflowWrap = 'anywhere';
  contentEl.style.wordBreak = 'break-word';

  if (titleText) {
    const titleEl = document.createElement('div');
    titleEl.textContent = titleText;
    titleEl.style.fontWeight = '600';
    titleEl.style.lineHeight = '1.2';
    contentEl.appendChild(titleEl);
  }

  if (bodyText) {
    const bodyEl = document.createElement('div');
    bodyEl.textContent = bodyText;
    bodyEl.style.fontWeight = '500';
    bodyEl.style.opacity = '0.92';
    bodyEl.style.lineHeight = '1.2';
    bodyEl.style.whiteSpace = 'pre-wrap';
    contentEl.appendChild(bodyEl);
  }

  // Botão fechar (aparece sempre; útil quando timeout=0)
  const closeEl = document.createElement('button');
  closeEl.type = 'button';
  closeEl.setAttribute('aria-label', 'Fechar');
  closeEl.textContent = '×';
  closeEl.style.border = '1px solid var(--border, rgba(255,255,255,0.2))';
  closeEl.style.background = 'transparent';
  closeEl.style.color = 'var(--text, #f1f5f9)';
  closeEl.style.fontSize = '16px';
  closeEl.style.lineHeight = '1';
  closeEl.style.cursor = 'pointer';
  closeEl.style.opacity = '0.9';
  closeEl.style.padding = '2px 8px';
  closeEl.style.borderRadius = 'var(--border-radius-md, 8px)';
  closeEl.style.transition = 'var(--transition-fast, all .2s ease)';
  closeEl.dataset.toastClose = '1';
  closeEl.addEventListener('mouseenter', () => {
    closeEl.style.opacity = '1';
    closeEl.style.background = 'var(--background-hover, rgba(255,255,255,0.06))';
  });
  closeEl.addEventListener('mouseleave', () => {
    closeEl.style.opacity = '0.9';
    closeEl.style.background = 'transparent';
  });
  closeEl.addEventListener('click', () => removeToast(container, toast));

  toast.appendChild(barEl);
  if (iconEl) toast.appendChild(iconEl);
  toast.appendChild(contentEl);
  toast.appendChild(closeEl);
  container.appendChild(toast);

  if (typeof onClick === 'function') {
    toast.style.cursor = 'pointer';
    toast.style.outline = '1px solid transparent';
    toast.style.outlineOffset = '2px';
    toast.tabIndex = 0;
    if (titleText || bodyText) {
      const label = titleText || bodyText;
      toast.setAttribute('aria-label', `${label}. Clique para abrir.`);
    }
    const baseShadow = toast.style.boxShadow;
    const applyInteractiveStyles = () => {
      toast.style.outline = '1px solid rgba(255, 255, 255, 0.28)';
      toast.style.boxShadow = '0 12px 34px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(255, 255, 255, 0.18)';
      toast.style.transform = 'translateY(-2px)';
    };
    const resetInteractiveStyles = () => {
      toast.style.outline = '1px solid transparent';
      toast.style.boxShadow = baseShadow;
      toast.style.transform = 'translateY(0)';
    };
    const handleToastClick = (event) => {
      if (event?.target?.closest?.('[data-toast-close="1"]')) {
        return;
      }
      try {
        onClick();
      } catch {
        // Callback do toast nao deve impedir a remocao visual.
      }
      removeToast(container, toast);
    };
    toast.addEventListener('click', handleToastClick);
    toast.addEventListener('mouseenter', applyInteractiveStyles);
    toast.addEventListener('mouseleave', resetInteractiveStyles);
    toast.addEventListener('focus', applyInteractiveStyles);
    toast.addEventListener('blur', resetInteractiveStyles);
    toast.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleToastClick(event);
      }
    });
  }

  // Animação de entrada
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });

  // Saída automática (se timeout > 0)
  if (timeout > 0) {
    setTimeout(() => removeToast(container, toast), timeout);
  }
}

export function showToast(
  message,
  {
    timeout = 3000,
    type = 'info', // 'info' | 'success' | 'warning' | 'error'
    title,
    description,
    iconUrl,
    iconAlt,
    onClick,
  } = {}
) {
  if (typeof document === 'undefined') return;
  const rawMessage = message === null || message === undefined ? '' : String(message);
  const titleText = typeof title === 'string' ? title.trim() : '';
  const descriptionText = typeof description === 'string' ? description.trim() : '';
  const bodyText = titleText ? (descriptionText || rawMessage) : rawMessage;
  if (!titleText && !bodyText) return;

  const container = ensureContainer();
  const dedupKey = [titleText, bodyText].filter(Boolean).join('\n');

  // dedup leve: evita múltiplos toasts IGUAIS visíveis ou aguardando fila.
  if (hasVisibleToast(container, dedupKey) || hasQueuedToast(dedupKey)) {
    return;
  }

  const toastData = {
    id: ++toastSequence,
    dedupKey,
    titleText,
    bodyText,
    type,
    iconUrl,
    iconAlt,
    onClick,
    timeout,
  };

  if (container.childElementCount < MAX_VISIBLE_TOASTS) {
    renderToast(container, toastData);
    return;
  }

  toastQueue.push(toastData);
  while (toastQueue.length > MAX_QUEUED_TOASTS) {
    toastQueue.shift();
  }
}

// Helpers por tipo — mantém compatibilidade e simplifica uso nos componentes
export function showSuccess(message, opts = {}) {
  return showToast(message, { ...opts, type: 'success' });
}

export function showError(message, opts = {}) {
  return showToast(message, { ...opts, type: 'error' });
}

export function showInfo(message, opts = {}) {
  return showToast(message, { ...opts, type: 'info' });
}

export function showWarning(message, opts = {}) {
  return showToast(message, { ...opts, type: 'warning' });
}
